<template>
  <div id="view-all-work-permits">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ title }}
          </span>
        </div>
        <div class="right-btns">
          <template v-if="!isHoistingAtLocationPage">
            <a
              v-if="showHoistingModule"
              class="name-hyper-link quick-search-link"
              @click="quickSearch(true,false,true)"
            >
              {{ threeDaysHoistingLabel }}
            </a>
            <a
              class="name-hyper-link quick-search-link"
              @click="quickSearch(true)"
            >
              {{ $t('workPermit.todayPermits') }}
            </a>
            <a
              class="name-hyper-link quick-search-link"
              @click="quickSearch(false, true)"
            >
              {{ $t('workPermit.nonApprPermits') }}
            </a>
          </template>
          <button
            type="button"
            class="btn btn--darkblue"
            @click="resetFilters()"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            :disabled="false"
            @click="search()"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>

    <div class="work-premits-view-all-table">
      <div style="display: inline; vertical-align: middle;margin-left: 15px;">
        <span>{{ $t('total') }}: {{ sortedItems.length }}</span>
      </div>
      <CommonTable
        ref="commonTable"
        :renderHeaderList="itemHeaders"
        :tableId="tableId"
        :minWidth1800px="false"
        :needSearch="true"
        :yScrollAuto="true"
        :tableContentLists="sortedItems"
        :defaultOrder="true"
        defaultOrderKey="duration"
        :interactionFunc="interactionFunc"
        :getConditions="getConditions"
        :defaultConditions="commonTableSearch"
        :enterEvent="search"
        :needSaveTableInfo="needSaveTableInfo"
        :orderMethods="orderMethods"
      />
    </div>
    <HKLRemindModal ref="remindEmptyModal">
      <template slot="title">
        {{ $t('hint.emptySearch') }}
      </template>
    </HKLRemindModal>
  </div>
</template>

<script>
import currencyFormatter from 'currency-formatter'
import lodash from 'lodash'
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { getCurrentInstance, computed, unref } from 'vue-demi'
import CommonTable from '@/controls/CommonTable'
import Sticky from '@/controls/Sticky'
import types from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import { getDuration, parsedWPFieldToShow, parseSearchFilterProjectType } from '@/modules/workPermits/utils'
import { navigate, getLastHistoryByStatusWithoutResume } from '@/api/workPermit/util'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES } from '@/modules/workPermits/consts'
import { buildAllWorkPermitsProjectTypeFilterOptions, buildAllWorkPermitsStatusFilterOptions, buildAllWorkPermitsTypeFilterOptions } from '@/modules/workPermits/allWorkPermitsListFilterOptions'
import { getPermitsAtLocationLabel, getThreeDaysHoistingLabel, getWorkPermitTypeLabel } from '@/modules/workPermits/regionalWorkPermitLabels'
import HKLRemindModal from '@/controls/Modal/HKLRemindModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import { useStoreGetters } from '@/store/index'
import i18n from '@/utils/i18n'
import { durationPermitCodeSortKey, permitCodeSortKey } from '@/utils/sortingHelper'

const sortingAttributes = {
  type: null,
  building: null,
  location: null,
  floors: null,
  contractor: null,
  workType: null,
  code: null,
  status: null,
  startDate: null,
  durationStart: null,
  durationEnd: null,
  endDate: null,
  isolation: null,
  showHotWork: null,
}

export default {
  name: 'AllWorkPermits',
  components: {
    Sticky,
    CommonTable,
    HKLRemindModal,
  },
  setup () {
    const { proxy: that } = getCurrentInstance()
    let returnData = {}
    const checkIsEmptyFilter = () => {
      const obj = lodash.fromPairs(
        that.itemHeaders.map((itemHeader) => [itemHeader.name, lodash.get(that.sortingAttributes, itemHeader.name)]),
      )
      obj['contractorName'] = that.sortingAttributes['contractor']
      obj['duration'] = that.sortingAttributes['startDate'] || that.sortingAttributes['endDate']

      const result = Object.values(obj).some((v) => {
        if (Array.isArray(v) || typeof v === 'string') return !!v.length
        return !!v
      })
      return !result
    }
    const { open: remindEmptyModalOpen, modal: remindEmptyModal } = useRefModal()
    const { backEndRegionalEnv } = useStoreGetters('regions', ['backEndRegionalEnv'])
    const showHoistingModule = computed(() => {
      return !!lodash.get(unref(backEndRegionalEnv), 'moduleSwitch.hoistingModule')
    })
    const ALL = i18n.t('all')
    const typeOptions = computed(() => buildAllWorkPermitsTypeFilterOptions(unref(showHoistingModule)))

    const projectTypeOptions = computed(() => buildAllWorkPermitsProjectTypeFilterOptions())
    returnData = Object.assign(returnData, {
      typeOptions,
      projectTypeOptions,
      ALL,
      showHoistingModule,
      checkIsEmptyFilter,
      remindEmptyModalOpen,
      remindEmptyModal,
    })
    return returnData
  },
  data () {
    return {
      buildingOptions: [],
      tableId: 'work-premits-view-all',
      currencyFormatter,
      emptyValue: this.$t('emptyValue'),
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      noStore: false,
      innerWorkPermits: [],
      isolation: '',
      showHotWork: null,
      commonTableSearch: '',
      needSaveTableInfo: true,
      interactionFunc: {
        code: this.navToPage,
      },
      orderMethods: {
        code: this.orderByPermitCode,
        floors: this.orderByFloor,
        duration: this.orderByDuration,
      },
    }
  },
  computed: {
    ...mapGetters('user', ['isEngineer', 'isTenant', 'isContractor', 'buildings']),
    ...mapGetters('workPermit', ['lastSearchedAttributes', 'allWorkPermits', 'quickSearchEnabled']),
    title () {
      return this.isHoistingAtLocationPage ? getPermitsAtLocationLabel() : this.$t('workPermit.viewAll.title')
    },
    threeDaysHoistingLabel () {
      return getThreeDaysHoistingLabel()
    },
    statusOptions () {
      return buildAllWorkPermitsStatusFilterOptions(this.isEngineer)
    },
    sortedItems () {
      let data = this.noStore ? this.innerWorkPermits : this.allWorkPermits
      if (!data || !data.length) return []
      let result = data.map(item => {
        let isolationText = this.emptyValue
        if (this.isHoistingAtLocationPage) {
          const isolation = getLastHistoryByStatusWithoutResume(item.histories, WORK_PERMIT_STATUS.REVIEWED) || getLastHistoryByStatusWithoutResume(item.histories, WORK_PERMIT_STATUS.APPR) || getLastHistoryByStatusWithoutResume(item.histories, WORK_PERMIT_STATUS.REJECTED)
          isolationText = (lodash.get(isolation, 'detail.isolatedDevicesOptionsText') || this.emptyValue).replace(/\n/g, '<br>')
        }

        const parsedFields = parsedWPFieldToShow(item)
        return {
          originData: item,
          code: item.code,
          contractorName: `${lodash.get(item, 'contractor.name')} (${parsedFields.tenantName})`,
          type: item.type ? getWorkPermitTypeLabel(item.type) : '',
          showHotWork: this.hotWorkRequired(item) ? ' <img src="/ic_hotwork.png" alt="hotwork" />' : this.emptyValue,
          workDescription: this.getWorkDescription(item),
          duration: {
            startDate: item.startDate,
            endDate: item.endDate,
          },
          status: item.status,
          isolation: isolationText,
          allowLink: true,
          startDate: item.startDate,
          ...parsedFields,
        }
      })
      return result
    },
    itemHeaders () {
      const typeColumnName = 'type'
      let headers = [
        {
          name: 'code',
          title: this.$t('workPermit.number'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'contractorName',
          title: this.$t('workPermit.contractor'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'PIC',
          title: this.$t('ProjectPIC'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'projectType',
          title: this.$t('workPermit.projectType'),
          inputType: 'select',
          multiLabel: 'label',
          options: this.projectTypeOptions,
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: typeColumnName,
          title: this.$t('workPermit.type'),
          inputType: 'select',
          formatRule: this.formatWPType,
          searchable: true,
          multiLabel: 'label',
          options: this.typeOptions,
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'workDescription',
          title: this.$t('workPermit.description'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'building',
          title: this.$t('workPermit.building'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'floors',
          title: this.$t('workPermit.floor'),
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'location',
          title: `${this.$t('workPermit.location')} (${this.$t('workPermit.workArea')})`,
          inputType: 'input',
          defaultValue: this.$t('emptyValue'),
        },
        {
          name: 'status',
          title: this.$t('status.name'),
          searchable: true,
          inputType: 'select',
          multiLabel: 'label',
          options: this.statusOptions,
          defaultValue: this.$t('emptyValue'),
          disabledSearch: this.quickSearchEnabled,
          customLabel: this.$options.filters.customStatusLabel,
          formatRule: val => this.$options.filters.customStatusLabel(val),
        },
        {
          name: 'duration',
          title: this.$t('workPermit.duration'),
          inputType: 'date',
          disabledSearch: this.quickSearchEnabled,
          disabledDate: false,
          datePickerCanPutTotop: false,
          disabledDateRangeBySelect: true,
          formatRule: getDuration,
        },
      ]
      if (this.isHoistingAtLocationPage) {
        // replace type with showHotWork column
        let typeColumnIndex = headers.findIndex(item => item.name === typeColumnName)
        headers.splice(typeColumnIndex, 1, {
          name: 'showHotWork',
          title: this.$t('workPermit.showHotWork'),
          inputType: 'select',
          options: [this.$t('all'), this.$t('yes'), this.$t('no')],
          defaultValue: this.$t('emptyValue'),
        })
        headers.push({
          name: 'isolation',
          title: this.$t('workPermit.isolation'),
          inputType: 'input',
        })
      }
      return headers
    },
    isHoistingAtLocationPage () {
      return this.$route.name === 'hoistingAtLocation'
    },
  },
  watch: {
    '$route': function () {
      this.tableId = this.isHoistingAtLocationPage ? `hoisting-view-all` : 'work-premits-view-all'
    },
  },
  async mounted () {
    $('#search-panel').keydown(function (event) {
      var keypressed = event.keyCode || event.which
      if (keypressed === 13) {
        $('#query-btn').click()
      }
    })
    const lastSearchAttributes = lodash.cloneDeep(this.lastSearchedAttributes)
    if (Object.keys(lastSearchAttributes).length > 0) {
      this.sortingAttributes = lastSearchAttributes
    }
    const routeQuery = this.$route.query
    if (this.isHoistingAtLocationPage) {
      this.needSaveTableInfo = false
      let attrs = lodash.cloneDeep(routeQuery)
      attrs.status = {
        label: WORK_PERMIT_STATUS[routeQuery.status],
        val: WORK_PERMIT_STATUS[routeQuery.status],
      }
      attrs.type = {
        label: this.$options.filters.capitalize(WORK_PERMIT_TYPES[routeQuery.type]),
        val: [WORK_PERMIT_TYPES[routeQuery.type]],
      }
      attrs.startDate = routeQuery.startDate && new Date(+routeQuery.startDate)
      attrs.endDate = routeQuery.endDate && new Date(+routeQuery.endDate)
      this.commonTableSearch = {
        building: {
          inputType: 'input',
          value: attrs.building,
        },
        floors: {
          inputType: 'input',
          value: attrs.floors,
        },
        status: {
          inputType: 'select',
          value: attrs.status,
        },
        duration: {
          inputType: 'date',
          value: [attrs.startDate, attrs.endDate],
        },
      }
      this.setSortingAttributes(attrs)
      this.search()
    }
    this.tableId = this.isHoistingAtLocationPage ? `hoisting-view-all` : 'work-premits-view-all'
    if (this.$route.query.tag === 'today') {
      await this.$nextTick()
      this.quickSearch()
      navigation.replacePage('allWorkPermits')
    }
  },
  methods: {
    ...mapMutations('workPermit', {
      saveSearchAttrs: types.WORKPERMIT_SET_SEARCH_ATTRS,
      setQuickSearch: types.WORKPERMIT_SET_QUICKSEARCH,
    }),
    ...mapActions('workPermit', [
      'getAllWorkPermits',
      'getAllWPListByPagination',
    ]),
    hotWorkRequired (item) {
      return item.type === WORK_PERMIT_TYPES.HOISTING && item.hotWork
    },
    formatWPType (info, wp) {
      return wp.showHotWork !== this.emptyValue ? info + wp.showHotWork : info
    },
    getWorkDescription (item) {
      let description = item.workType
      if (item.type === WORK_PERMIT_TYPES.HOISTING) {
        description = item.description
      }
      return description
    },
    navToPage (item) {
      let permit, hoistingId
      const originData = item.originData
      if (originData.type === WORK_PERMIT_TYPES.HOISTING) {
        permit = originData
        hoistingId = originData.id
      } else {
        permit = originData
      }
      navigate(permit, hoistingId, { fromViewAll: true })
    },
    async resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.commonTableSearch = ''
      this.setQuickSearch(false)
      this.saveSearchAttrs({})
      await this.$nextTick()
      const table = this.$refs.commonTable
      if (!table) return
      table.resetFilterUiOnly()
    },
    async quickSearch (todayQuickSearch = true, nonApprQuickSearch = false, threeDaysHoisting = false) {
      await this.resetFilters()
      this.$refs.commonTable.changeKeyAndOrder('contractor', true)
      this.setQuickSearch(true)
      const days = threeDaysHoisting ? 3 : 1
      this.sortingAttributes['durationStart'] = new Date(moment().hours(0).minutes(0).seconds(0).milliseconds(0))
      this.sortingAttributes['durationEnd'] = new Date(moment().add(days, 'days').hours(5).minutes(59).seconds(59).milliseconds(0))

      if (todayQuickSearch) {
        this.sortingAttributes.status = {
          val: [WORK_PERMIT_STATUS.APPR, WORK_PERMIT_STATUS.INPROGRESS],
        }
      }
      if (nonApprQuickSearch) {
        this.sortingAttributes.status = {
          val: [
            WORK_PERMIT_STATUS.WAPPR,
            WORK_PERMIT_STATUS.REJECTED,
            WORK_PERMIT_STATUS.ACKNOWLEDGED,
            WORK_PERMIT_STATUS.REVIEWED,
            WORK_PERMIT_STATUS.REVIEWED2,
            WORK_PERMIT_STATUS.SUSPENDED,
            WORK_PERMIT_STATUS.ENDED,
          ],
        }
      }

      if (threeDaysHoisting) {
        this.sortingAttributes.type = {
          val: [WORK_PERMIT_TYPES.HOISTING],
        }
      }
      await this.search()
    },
    async search () {
      if (!this.noStore) {
        this.saveSearchAttrs(lodash.cloneDeep(this.sortingAttributes))
      }
      if (this.checkIsEmptyFilter()) return this.remindEmptyModalOpen()
      let filters = lodash.cloneDeep(this.sortingAttributes)
      filters.type = this.sortingAttributes.type && lodash.get(this.sortingAttributes, 'type.label') !== this.ALL
        ? lodash.get(this.sortingAttributes, 'type.val').join(',') : null
      filters.status = this.sortingAttributes.status && lodash.get(this.sortingAttributes, 'status.label') !== this.ALL
        ? lodash.get(this.sortingAttributes, 'status.val') : null
      filters.limitAttrs = true
      filters = {
        ...filters,
        ...parseSearchFilterProjectType(lodash.get(filters, 'projectType.val')),
      }

      if (filters.startDate) {
        filters.viewAllStartDate = filters.startDate
        delete filters.startDate
      }
      if (filters.endDate) {
        filters.viewAllEndDate = new Date(moment(filters.endDate).endOf('day').unix() * 1000)
        delete filters.endDate
      }
      if (filters.floors) {
        this.addAllFloorTag(filters)
      }
      filters.excludeModels = {'workers': true}
      if (!this.isHoistingAtLocationPage) {
        filters.excludeModels.histories = true
      }
      const options = {
        filters: lodash.pickBy(filters, lodash.identity),
        quickSearch: this.quickSearchEnabled,
        keep: !this.noStore,
      }
      const getWPFunc = this.quickSearchEnabled ? 'getAllWorkPermits' : 'getAllWPListByPagination'
      this.innerWorkPermits = await this[getWPFunc](options)
    },
    addAllFloorTag (filters) {
      if (filters.floors.toUpperCase().includes('ALL') || filters.floors.toUpperCase().includes('FLOORS')) {
        filters['isAllFloor'] = true
      }
    },
    setSortingAttributes (attrs) {
      Object.assign(this.sortingAttributes, sortingAttributes, attrs)
    },
    setNoStore (bool) {
      this.noStore = bool
    },
    getConditions (conditions) {
      lodash.map(conditions, (condition, key) => {
        if (condition) {
          const value = typeof condition.value === 'string' ? condition.value.trim() : condition.value
          switch (key) {
            case 'duration':
              this.sortingAttributes['startDate'] = value[0]
              this.sortingAttributes['endDate'] = value[1]
              break
            case 'contractorName':
              this.sortingAttributes['contractor'] = value
              break
            default:
              this.sortingAttributes[key] = value
              break
          }
        }
      })
    },
    orderByFloor (item, order, orderKey) {
      if (item.floors) {
        return item.floorIndex
      } else {
        return order ? item.floorIndex : Number.MIN_SAFE_INTEGER
      }
    },
    orderByDuration (item, order, orderKey) {
      return durationPermitCodeSortKey(item.startDate, item.code)
    },
    orderByPermitCode (item) {
      return permitCodeSortKey(item.code)
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
#view-all-work-permits {
  height: calc(100vh - 126px);
  & ::v-deep .header-panel {
    height: 70px;
  }
  & ::v-deep .common-header-filter th {
    font-weight: 400;

    label {
      font-weight: 400;
    }
  }
}
.right-btns {
  .quick-search-link {
    margin-right: 15px;
    font-weight: bold;
  }
}
.work-premits-view-all-table {
  height: calc(100% - 70px);
}
.work-premits-view-all_common-table {
  padding-bottom: 6px;
  & ::v-deep .common-table-header-container {
    min-width: 2400px
  }
  & ::v-deep .location {
    min-width: 300px;
    width: calc(100% - 1955px)
  }
  & ::v-deep .type {
    width: 170px;
  }
  & ::v-deep .projectType {
    width: 170px;
  }
  & ::v-deep .building, & ::v-deep .floors {
    width: 150px;
  }
  /* Restore filter-row column widths when CommonTable (PMS-157) sets .common-header-filter th { min-width: 0 }. */
  & ::v-deep .common-header-filter {
    th.type,
    th.projectType {
      min-width: 170px;
      max-width: 170px;
    }
    th.building,
    th.floors {
      min-width: 150px;
      max-width: 150px;
    }
    th.location {
      min-width: 300px;
      width: calc(100% - 1955px);
    }
  }
}
.hoisting-view-all_common-table {
  & ::v-deep .common-table-header-container{
    min-width: 2600px
  }
  & ::v-deep .location {
    min-width: 300px;
    width: calc(100% - 2180px)
  }
  & ::v-deep .projectType {
    width: 170px;
  }
  & ::v-deep .building {
    width: 140px;
  }
  & ::v-deep .floors {
    width: 100px;
  }
  & ::v-deep .isolation {
    width: 350px;
  }
  & ::v-deep .showHotWork {
    width: 150px;
  }
  & ::v-deep .common-header-filter {
    th.projectType {
      min-width: 170px;
      max-width: 170px;
    }
    th.building {
      min-width: 140px;
      max-width: 140px;
    }
    th.floors {
      min-width: 100px;
      max-width: 100px;
    }
    th.location {
      min-width: 300px;
      width: calc(100% - 2180px);
    }
    th.isolation {
      min-width: 350px;
      max-width: 350px;
    }
    th.showHotWork {
      min-width: 150px;
      max-width: 150px;
    }
  }
}
.common-table {
  height: calc(100% - 38px);
  & ::v-deep .status {
    width: 205px;
  }
  & ::v-deep .contractorName {
    width: 210px;
  }
  & ::v-deep .PIC {
    width: 150px;
  }
  & ::v-deep .workDescription {
    width: 200px;
  }
  & ::v-deep .code {
    width: 200px;
  }

  & ::v-deep .duration {
    width: 300px;
  }
  & ::v-deep .common-header-filter {
    th.code {
      min-width: 200px;
      max-width: 200px;
    }
    th.contractorName {
      min-width: 210px;
      max-width: 210px;
    }
    th.PIC {
      min-width: 150px;
      max-width: 150px;
    }
    th.workDescription {
      min-width: 200px;
      max-width: 200px;
    }
    th.status {
      min-width: 205px;
      max-width: 205px;
    }
    th.duration {
      min-width: 300px;
      max-width: 300px;
    }
  }
}
</style>
